import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const funnel: Competitor = {
  "id": "funnel",
  "name": "Funnel",
  "group": "ecomm",
  "website": "https://funnel.io",
  "owner": "Funnel (independent, founded 2014 in Stockholm); acquired measurement firm Adtriba in June 2024",
  "summary": "Funnel is a marketing data platform that collects and harmonizes data from 600+ marketing and sales sources, stores it, and sends it to dashboards, BI tools and warehouses, with an add-on measurement product (MMM, MTA, incrementality). It is built for in-house marketing teams and agencies at mid-size and larger brands.",
  "bestFor": [
    "Marketing teams with many ad platforms and accounts who want harmonized spend and performance data without a data engineer",
    "Brands that want MMM and multi-touch attribution from the same vendor that holds their marketing data",
    "Agencies reporting across many client accounts"
  ],
  "notFor": [
    "Small brands on a tight budget: plans start at $300/month billed annually and Snowflake export needs the Enterprise tier",
    "Teams whose main need is order-level finance reporting (refunds, fees, COGS, contribution margin) rather than marketing performance"
  ],
  "strengths": [
    "Very broad connector library (vendor cites 673 connectors), maintained by the vendor, including Shopify, Amazon Seller Central, TikTok Shop, Recharge and the main ad platforms",
    "Built-in harmonization (shared field definitions, currency conversion, budgets and targets) that a custom build would need dbt models for",
    "Native dashboards inside the product, plus Looker Studio and Google Sheets on every paid plan",
    "Funnel Measure adds MMM, data-driven MTA and incrementality testing; building these well in-house takes data-science work a typical warehouse project does not include",
    "Funnel AI agent can answer questions, create custom fields and build dashboards; MCP access available from Claude",
    "Pricing is not tied to data volume or row counts, so cost does not grow with order history"
  ],
  "limitations": [
    "Snowflake export is Enterprise-only; Starter has no BigQuery, Power BI or Tableau output",
    "Funnel Measure is a separate add-on from $2,250/month (Digital Measurement, at the default ad-spend bracket) and is not available on Starter",
    "Some connectors (for example Recharge) are data-request connectors that need a Business or Enterprise plan",
    "A third-party review notes a G2 reviewer calling it mainly an ETL tool, and rates setup as moderate to high because data needs mapping before use (competitor source, may be one-sided)",
    "No profit or COGS model was found in vendor docs, so contribution margin across Shopify, Amazon and wholesale still needs custom modeling"
  ],
  "pricing": {
    "model": "tiered subscription plus Flexpoints capacity (per connector, account and destination)",
    "publicStartingPrice": "From $300/month billed annually (Starter)",
    "freeTier": "no permanent free plan found; 14-day free trial of Data Hub, no credit card",
    "notes": "Tiers: Starter from $300/month annually (117 connectors, 13 destinations, daily exports, Looker Studio/Sheets/dashboards, no BigQuery/Power BI/Tableau); Business from $600/month annually (579 connectors, BigQuery/Redshift/S3/Azure, Power BI, Tableau, exports every 2 hours, unlimited users); Enterprise custom (Snowflake export, SSO/SCIM, audit log, EU data center). Cost driver is Flexpoints: connector 50 FP, account 5 FP, visualization destination 150 FP, warehouse destination 300 FP; data volume does not affect price. Vendor page does not state Flexpoints per plan; a third-party blog says Starter includes 400. Funnel Measure add-on: Digital Measurement from $2,250/month annually; Advanced Measurement custom. Third-party sources report the free plan closed to new users in Dec 2025 and Starter launched Feb 2026 with price changes in March 2026 (not confirmed on vendor site). Setup time: vendor gives none; Shopify backfill takes about an hour to several days per Funnel help docs.",
    "sourceUrl": "https://funnel.io/pricing?hsLang=en",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native connector for sales, order, inventory and customer data; one store per data source",
      "sourceUrl": "https://help.funnel.io/en/articles/9240193-shopify-connection-guide"
    },
    "amazon": {
      "value": "yes",
      "note": "Seller Central connector (orders, order items, sales and traffic, returns) on all plans",
      "sourceUrl": "https://funnel.io/data-sources/amazon-selling-partner"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "Listed in data sources",
      "sourceUrl": "https://funnel.io/all-data-sources"
    },
    "wholesale_retail_or_erp": {
      "value": "unknown",
      "note": "NetSuite, Faire, ShipStation not confirmed",
      "sourceUrl": "https://funnel.io/all-data-sources"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Facebook Ads, Google Ads, TikTok Ads and many others",
      "sourceUrl": "https://funnel.io/all-data-sources"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Data-driven MTA in Funnel Measure add-on; no own tracking pixel found",
      "sourceUrl": "https://funnel.io/measure"
    },
    "mmm_incrementality": {
      "value": "yes",
      "note": "Funnel Measure add-on (Business/Enterprise): MMM, incrementality testing on Advanced plan",
      "sourceUrl": "https://funnel.io/pricing?hsLang=en",
      "badge": "Paid add-on"
    },
    "profit_cogs": {
      "value": "unknown",
      "note": "No profit/COGS model found in vendor pages",
      "sourceUrl": "https://funnel.io/pricing?hsLang=en"
    },
    "ltv_cohorts": {
      "value": "unknown",
      "note": "Not verified",
      "sourceUrl": "https://funnel.io/pricing?hsLang=en"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "Recharge data via data request (Business/Enterprise); raw subscription and order fields, no prebuilt analytics verified",
      "sourceUrl": "https://help.funnel.io/en/articles/13455123-recharge-dimensions-and-metrics"
    },
    "warehouse_access": {
      "value": "partial",
      "note": "BigQuery/Redshift from Business; Snowflake only on Enterprise",
      "sourceUrl": "https://funnel.io/pricing?hsLang=en",
      "badge": "Snowflake on Enterprise"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Custom dimensions and metrics, field rules; Funnel AI can create them",
      "sourceUrl": "https://funnel.io/funnel-ai"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "No in-product SQL found",
      "sourceUrl": "https://funnel.io/funnel-ai"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Funnel AI agent (questions, fields, dashboards, scheduled analyses); listed on Starter plan",
      "sourceUrl": "https://funnel.io/pricing?hsLang=en"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "Not found",
      "sourceUrl": "https://funnel.io/funnel-ai"
    }
  },
  "recentChanges": [
    {
      "date": "2024-06",
      "change": "Acquired Adtriba, a Hamburg measurement firm (MMM, MTA, incrementality)",
      "sourceUrl": "https://funnel.io/press-releases/marketing-intelligence-platform-funnel-acquires-measurement-firm-adtriba"
    },
    {
      "date": "2025-12",
      "change": "Free plan closed to new users (third-party report)",
      "sourceUrl": "https://twominutereports.com/blog/funnel-io-pricing"
    },
    {
      "date": "2026-02",
      "change": "Starter plan launched; price adjusted March 2026 (third-party report)",
      "sourceUrl": "https://twominutereports.com/blog/funnel-io-pricing"
    },
    {
      "date": "2026-03",
      "change": "Funnel Measure split into Digital Measurement and Advanced Measurement plans; Data Chat AI updates",
      "sourceUrl": "https://help.funnel.io/en/articles/14115639-march-2026-release-notes"
    },
    {
      "date": "2026-08",
      "change": "Funnel AI can build dashboards; Funnel MCP in Claude connector directory; Slack agent private beta; Single Fit MMM",
      "sourceUrl": "https://help.funnel.io/en/articles/16527170-august-2026-release-notes"
    }
  ],
  "sources": [
    {
      "title": "Funnel plans and pricing",
      "url": "https://funnel.io/pricing?hsLang=en"
    },
    {
      "title": "Funnel data sources",
      "url": "https://funnel.io/all-data-sources"
    },
    {
      "title": "Funnel Measure",
      "url": "https://funnel.io/measure"
    },
    {
      "title": "Funnel AI",
      "url": "https://funnel.io/funnel-ai"
    },
    {
      "title": "Shopify connection guide",
      "url": "https://help.funnel.io/en/articles/9240193-shopify-connection-guide"
    },
    {
      "title": "Amazon Seller Central connector",
      "url": "https://funnel.io/data-sources/amazon-selling-partner"
    },
    {
      "title": "Recharge dimensions and metrics",
      "url": "https://help.funnel.io/en/articles/13455123-recharge-dimensions-and-metrics"
    },
    {
      "title": "March 2026 release notes",
      "url": "https://help.funnel.io/en/articles/14115639-march-2026-release-notes"
    },
    {
      "title": "August 2026 release notes",
      "url": "https://help.funnel.io/en/articles/16527170-august-2026-release-notes"
    },
    {
      "title": "Funnel acquires Adtriba (press release)",
      "url": "https://funnel.io/press-releases/marketing-intelligence-platform-funnel-acquires-measurement-firm-adtriba"
    },
    {
      "title": "Funnel.io pricing 2026 (Two Minute Reports, third-party competitor blog)",
      "url": "https://twominutereports.com/blog/funnel-io-pricing"
    }
  ],
  "lastReviewed": "2026-11-03"
};
